import { trpc } from "@/trpc";
import { useState, useEffect } from "react";
import { mapPipelineName } from "@/utils/valueMap";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { isDataScopedEmployee, type EmployeeRole } from "@/utils/permissions";

type PipelinePermissionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    name: string;
    email: string;
    roles: string[];
    pipelinePermissions?: Array<{ id: string }>;
  };
  onUpdate: () => void;
};

const PipelinePermissionsModal = ({ isOpen, onClose, user, onUpdate }: PipelinePermissionsModalProps) => {
  const [selectedPipelineIds, setSelectedPipelineIds] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<EmployeeRole>("basic_employee");

  const availablePipelines = trpc.auth.getAvailablePipelines.useQuery(undefined, {
    enabled: isOpen && isDataScopedEmployee([selectedRole]),
  });

  const updatePermissions = trpc.auth.updateMemberPipelinePermissions.useMutation();
  const updateRole = trpc.auth.updateMemberRole.useMutation();

  useEffect(() => {
    if (isOpen) {
      // Set initial role
      if (user.roles.includes("admin_employee")) {
        setSelectedRole("admin_employee");
      } else if (user.roles.includes("restricted_employee")) {
        setSelectedRole("restricted_employee");
      } else {
        setSelectedRole("basic_employee");
      }
      // Set initial pipeline permissions
      if (user.pipelinePermissions) {
        setSelectedPipelineIds(user.pipelinePermissions.map((pipeline) => pipeline.id));
      }
    }
  }, [isOpen, user]);

  const handleTogglePipeline = (pipelineId: string) => {
    setSelectedPipelineIds((prev) =>
      prev.includes(pipelineId) ? prev.filter((id) => id !== pipelineId) : [...prev, pipelineId]
    );
  };

  const handleSave = async () => {
    try {
      // Update role if it changed
      const currentRole: EmployeeRole = user.roles.includes("admin_employee")
        ? "admin_employee"
        : user.roles.includes("restricted_employee")
        ? "restricted_employee"
        : "basic_employee";

      if (currentRole !== selectedRole) {
        await updateRole.mutateAsync({
          memberId: user.id,
          role: selectedRole,
        });
      }

      // Update pipeline permissions for pipeline-scoped roles (basic or restricted)
      if (isDataScopedEmployee([selectedRole])) {
        await updatePermissions.mutateAsync({
          memberId: user.id,
          pipelineIds: selectedPipelineIds,
        });
      }

      toast.success("Usuario actualizado", {
        description: "El rol y permisos se actualizaron correctamente",
      });
      onUpdate();
      onClose();
    } catch (error: any) {
      toast.error("Error al actualizar", {
        description: error.message || "No se pudo actualizar el usuario",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Usuario</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div>
            <p className="font-medium text-gray-700">
              {user.name || "Sin nombre"}
            </p>
            <p className="text-sm text-gray-600">
              {user.email}
            </p>
          </div>

          <Separator />

          {/* Role Selection */}
          <div>
            <label className="mb-2 block font-semibold text-gray-800">
              Rol
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as EmployeeRole)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="restricted_employee">Empleado Restringido</option>
              <option value="basic_employee">Empleado Básico</option>
              <option value="admin_employee">Empleado Administrador</option>
            </select>
          </div>

          {/* Pipeline Permissions - Only show for pipeline-scoped roles (basic or restricted) */}
          {isDataScopedEmployee([selectedRole]) && (
            <>
              <Separator />
              <div>
                <p className="mb-3 font-semibold text-gray-800">
                  Permisos de Pipelines
                </p>
                <p className="mb-3 text-sm text-gray-600">
                  Selecciona los pipelines a los que tendrá acceso:
                </p>

                {availablePipelines.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : availablePipelines.data && availablePipelines.data.length > 0 ? (
                  <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto pr-2">
                    {availablePipelines.data.map((pipeline) => (
                      <div
                        key={pipeline.id}
                        className={cn(
                          "rounded-md border p-3 transition-all hover:border-purple-300",
                          selectedPipelineIds.includes(pipeline.id)
                            ? "border-purple-300 bg-purple-50"
                            : "border-gray-200 bg-white"
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <Checkbox
                            checked={selectedPipelineIds.includes(pipeline.id)}
                            onCheckedChange={() => handleTogglePipeline(pipeline.id)}
                            className="mt-0.5"
                          />
                          <div className="flex flex-col gap-0">
                            <p className="text-sm font-medium">
                              {mapPipelineName[pipeline.id as keyof typeof mapPipelineName] || pipeline.id}
                            </p>
                            <p className="text-xs text-gray-500">
                              ID: {pipeline.id}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    No hay pipelines disponibles
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="bg-[#5C28F5] text-white hover:bg-[#4a1fd4]"
            onClick={handleSave}
            disabled={
              updatePermissions.isLoading ||
              updateRole.isLoading ||
              (isDataScopedEmployee([selectedRole]) && availablePipelines.isLoading)
            }
          >
            {(updatePermissions.isLoading || updateRole.isLoading) && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PipelinePermissionsModal;
